/**
 * Modelos — carrega os .glb de /assets/models e entrega copias prontas.
 *
 * Os modelos sao do "GameDev Starter Kit - Farming" da AssetHunts, licenca
 * Personal do Fab. Estilo toon, baixo poly, feitos para jogo — e a linguagem
 * visual que o responsavel pediu.
 *
 * TEXTURA COMPARTILHADA (importante):
 * Todos os modelos usam a MESMA paleta de cores. Os .glb vieram com essa paleta
 * embutida em cada arquivo, o que daria 58 texturas distintas na GPU e
 * estouraria o limite de 8 da secao 12. Entao a paleta foi removida dos
 * modelos e e carregada UMA vez aqui, sendo aplicada em todos. Resultado:
 * 1 textura na GPU para a fazenda inteira.
 *
 * Cada modelo e carregado uma vez e guardado. Quem pede recebe uma copia, para
 * duas arvores nunca compartilharem o mesmo objeto de cena.
 *
 * P5: se um modelo faltar, o erro aparece no console em portugues dizendo qual
 * arquivo faltou e onde ele deveria estar.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as clonarComEsqueleto } from 'three/examples/jsm/utils/SkeletonUtils.js';

const CAMINHO = '/assets/models';
const PALETA = `${CAMINHO}/paleta.png`;

// A grade nativa deste pacote e de 2 unidades: um tile de chao mede 2x2.
export const TILE = 2;

/**
 * Altura da superficie do terreno.
 *
 * Os blocos de chao vao de Y=0 a Y=2 — eles sao CUBOS, nao tapetes. Entao a
 * grama fica em Y=2, e e ali que tudo se apoia. Colocar um predio em Y=0 o
 * enterra dois metros: foi o que aconteceu na primeira montagem, e explicava
 * os predios disformes e o cavalo atravessando o celeiro.
 */
export const ALTURA_SOLO = 2;

/**
 * Modelos que o jogo usa, agrupados pelo papel que cumprem.
 * O nome da esquerda e como o codigo se refere a peca; o da direita e o arquivo.
 */
export const MODELOS = {
  // --- terreno ---
  // Os "Ground" sao as CAMADAS DE TERRA do bloco — areia, barro, terra funda.
  // Nenhum deles e verde. A grama e o "Top", a tampa que vai em cima.
  // Medido lendo a cor que cada um busca no atlas: Ground_02 da #ff9e52
  // (laranja) e Top_01 da #8fa800 (verde).
  chao_terra: '3D_Tile_Ground_02',
  grama: '3D_Tile_Top_01',
  terra_arada: '3D_Tile_Farm_Field_01',
  agua: '3D_Tile_Water_01',

  // --- construcoes ---
  celeiro: 'Farm_House_Barn_01',
  silo: 'Farm_House_Silo_01',

  // --- o que cresce: 3 estagios por cultura ---
  milho_1: 'Farm_Crop_Corn_Step_01',
  milho_2: 'Farm_Crop_Corn_Step_02',
  milho_3: 'Farm_Crop_Corn_Step_03',
  cenoura_1: 'Farm_Crop_Carrot_Step_01',
  cenoura_2: 'Farm_Crop_Carrot_Step_02',
  cenoura_3: 'Farm_Crop_Carrot_Step_03',
  girassol_1: 'Farm_Crop_Sunflower_Step_01',
  girassol_2: 'Farm_Crop_Sunflower_Step_02',
  girassol_3: 'Farm_Crop_Sunflower_Step_03',
  brocolis_1: 'Farm_Crop_Broccoli_Step_01',
  brocolis_2: 'Farm_Crop_Broccoli_Step_02',
  brocolis_3: 'Farm_Crop_Broccoli_Step_03',

  // --- colhidos, para separar e distribuir ---
  colhido_milho: 'Farm_Crop_Corn_01',
  colhido_cenoura: 'Farm_Crop_Carrot_01',
  colhido_girassol: 'Farm_Crop_Sunflower_01',
  colhido_brocolis: 'Farm_Crop_Broccoli_01',
  colhido_couveflor: 'Farm_Crop_Cauliflower_01',
  colhido_cogumelo: 'Farm_Crop_Mashroom_02',

  // --- placas com desenho da cultura, para as tarefas de leitura ---
  placa_milho: 'Farm_Crop_Sign_Corn_01',
  placa_cenoura: 'Farm_Crop_Sign_Carrot_01',
  placa_girassol: 'Farm_Crop_Sign_Sunflower_01',
  placa_brocolis: 'Farm_Crop_Sign_Broccoli_01',
  placa_couveflor: 'Farm_Crop_Sign_Cauliflower_01',
  placa_cogumelo: 'Farm_Crop_Sign_Mashroom_02',

  // --- cercas ---
  cerca_a: 'Farm_Fence_C_01',
  cerca_b: 'Farm_Fence_C_02',
  cerca_c: 'Farm_Fence_C_03',

  // --- ferramentas ---
  pa: 'Farm_Tool_Shovel_01',
  foice: 'Farm_Tool_Sickle_01',
  regador: 'Farm_Tool_Watering_Pot_01',

  // --- natureza ---
  arvore: 'Farm_Crop_Tree_02',
  pinheiro: 'Plant_Pine_01',
  pinheiro_b: 'Plant_Pine_01A',
  pedra_a: 'Rock_A_01',
  pedra_b: 'Rock_A_02',
  pedra_c: 'Rock_A_03',
  maca: 'Farm_Tree_Product_Apple_01',
  laranja: 'Farm_Tree_Product_Orange_01',

  // --- objetos ---
  balde: 'Props_Bucket_01',
  caixote: 'Props_Crate_01',
  barril: 'Props_Wooden_Barrel_01',
  toco: 'Props_Tree_Stump_01',
  caixa_correio: 'Props_Mailbox_01',
  placa_madeira: 'Props_Wooden_Sign_01',
  ponte: 'Props_Broken_Rope_Bridge_01',

  // --- ceu ---
  nuvem_a: 'Cloud_01',
  nuvem_b: 'Cloud_02',
  nuvem_c: 'Cloud_03',

  // --- o ponteiro que mostra onde tocar ---
  ponteiro: 'Selection_Pointer_01',
  ponteiro_b: 'Selection_Pointer_02',
};

/**
 * Modelos que NAO usam a paleta compartilhada: cada um traz a propria textura.
 * Sao 3 texturas a mais, somando 4 no total — dentro do limite de 8 da secao 12.
 */
export const COM_TEXTURA_PROPRIA = {
  casa: 'farmhouse_free',
  pacote_animais: 'Animals_FREE',
  pacote_pessoas: 'Creative_Character_free',
};

/**
 * Animais dentro do pacote.
 *
 * O nome no arquivo .glb e "chicken.001", mas o Three.js LIMPA os nomes ao
 * carregar: pontos e colchetes somem, porque esses caracteres tem significado
 * no sistema de animacao. Entao dentro da cena o no se chama "chicken001".
 * Guardamos o prefixo e procuramos por ele, o que funciona nas duas formas e
 * sobrevive a uma troca de versao do pacote.
 */
export const ANIMAIS = {
  galinha: 'chicken',
  cachorro: 'dog',
  cavalo: 'horse',
  gato: 'kitty',
};

/** Acha um no cujo nome comeca com o prefixo, ignorando pontos e maiusculas. */
function acharPorPrefixo(raiz, prefixo) {
  const alvo = prefixo.toLowerCase();
  let achado = null;

  raiz.traverse((o) => {
    if (achado || !o.name) return;
    const limpo = o.name.toLowerCase().replace(/[._\s-]/g, '');
    if (limpo.startsWith(alvo)) achado = o;
  });

  return achado;
}

const carregador = new GLTFLoader();
const cache = new Map();
let paleta = null;

/** Carrega a paleta de cores compartilhada por todos os modelos. */
function carregarPaleta() {
  if (paleta) return paleta;

  paleta = new THREE.TextureLoader().load(PALETA, undefined, undefined, () => {
    console.error(
      `FAZENDA: nao consegui carregar a paleta de cores em ${PALETA}. ` +
        'Sem ela todos os modelos ficam brancos. Confira se o arquivo existe.'
    );
  });

  // glTF nao inverte o eixo V da textura; sem isto as cores saem trocadas.
  paleta.flipY = false;
  paleta.colorSpace = THREE.SRGBColorSpace;
  // A paleta e um mosaico de cores chapadas: filtrar borraria as bordas.
  paleta.magFilter = THREE.NearestFilter;
  paleta.minFilter = THREE.NearestFilter;
  paleta.generateMipmaps = false;

  return paleta;
}

/**
 * Carrega um modelo (ou devolve o que ja esta na memoria).
 * @param {boolean} usarPaleta  false para modelos que trazem textura propria
 */
function carregar(arquivo, usarPaleta = true) {
  if (cache.has(arquivo)) return cache.get(arquivo);

  const textura = usarPaleta ? carregarPaleta() : null;

  const promessa = new Promise((resolver, rejeitar) => {
    carregador.load(
      `${CAMINHO}/${arquivo}.glb`,
      (gltf) => {
        const raiz = gltf.scene;

        raiz.traverse((objeto) => {
          if (!objeto.isMesh) return;
          objeto.castShadow = true;
          objeto.receiveShadow = true;
          if (textura) {
            // Modelos da AssetHunts: todos apontam para a MESMA paleta.
            objeto.material.map = textura;
            objeto.material.color = new THREE.Color(0xffffff);
            objeto.material.needsUpdate = true;
          }
        });

        resolver(raiz);
      },
      undefined,
      (erro) =>
        rejeitar(
          new Error(
            `FAZENDA: nao consegui carregar o modelo "${arquivo}". ` +
              `Confira se ${CAMINHO}/${arquivo}.glb existe. (${erro?.message ?? erro})`
          )
        )
    );
  });

  cache.set(arquivo, promessa);
  return promessa;
}

/**
 * Copia de um modelo ja carregado.
 * @param {string} papel  uma chave de MODELOS (ex.: 'celeiro', 'milho_2')
 */
export function clonar(papel) {
  const arquivo = MODELOS[papel] ?? COM_TEXTURA_PROPRIA[papel] ?? papel;
  const promessa = cache.get(arquivo);

  if (!promessa) {
    throw new Error(
      `FAZENDA: pedi uma copia de "${papel}" antes de carrega-lo. ` +
        'Todos os modelos entram no precarregamento — confira o mapa MODELOS.'
    );
  }
  if (!promessa.modeloPronto) {
    throw new Error(
      `FAZENDA: o modelo "${papel}" ainda esta carregando. ` +
        'Espere o precarregamento terminar antes de montar o cenario.'
    );
  }

  const copia = promessa.modeloPronto.clone(true);
  copia.name = papel;
  return copia;
}

/**
 * Copia de um animal do pacote.
 * Os animais tem esqueleto, entao a copia precisa do SkeletonUtils —
 * um clone comum quebraria a ligacao entre a malha e os ossos.
 * @param {string} papel  uma chave de ANIMAIS (ex.: 'galinha')
 */
export function clonarAnimal(papel) {
  const nomeNoPacote = ANIMAIS[papel];
  if (!nomeNoPacote) {
    throw new Error(
      `FAZENDA: o animal "${papel}" nao existe. ` +
        `Disponiveis: ${Object.keys(ANIMAIS).join(', ')}.`
    );
  }

  const pacote = cache.get(COM_TEXTURA_PROPRIA.pacote_animais)?.modeloPronto;
  if (!pacote) {
    throw new Error('FAZENDA: o pacote de animais ainda nao carregou.');
  }

  const original = acharPorPrefixo(pacote, nomeNoPacote);
  if (!original) {
    const disponiveis = [];
    pacote.traverse((o) => { if (o.name && o.parent === pacote) disponiveis.push(o.name); });
    throw new Error(
      `FAZENDA: nao achei nenhum no comecando com "${nomeNoPacote}" dentro de ` +
        `Animals_FREE.glb. Nos disponiveis: ${disponiveis.join(', ')}. ` +
        'O pacote pode ter mudado de versao.'
    );
  }

  const copia = clonarComEsqueleto(original);
  copia.name = papel;
  copia.visible = true;
  return copia;
}

/**
 * Copia de um personagem humano.
 * O pacote e modular: um esqueleto so, com varias malhas de corpo penduradas.
 * Pegamos UMA malha por personagem — sao pessoas diferentes, nao pecas de
 * montar, e a crianca precisa reconhecer cada uma.
 * @param {number} indice  qual malha de corpo usar
 */
export function clonarPessoa(indice = 0) {
  const pacote = cache.get(COM_TEXTURA_PROPRIA.pacote_pessoas)?.modeloPronto;
  if (!pacote) {
    throw new Error('FAZENDA: o pacote de personagens ainda nao carregou.');
  }

  const copia = clonarComEsqueleto(pacote);

  // Mostra so a malha escolhida; o resto do pacote fica escondido.
  const corpos = [];
  copia.traverse((o) => {
    if (o.isMesh || o.isSkinnedMesh) corpos.push(o);
  });

  if (corpos.length === 0) {
    throw new Error('FAZENDA: o pacote de personagens nao tem nenhuma malha.');
  }

  const escolhido = corpos[indice % corpos.length];
  for (const c of corpos) c.visible = c === escolhido;

  copia.name = `pessoa_${indice}`;
  return copia;
}

/** Carrega tudo. Chame antes de montar o cenario. */
export async function prepararCache(aoProgredir) {
  const comPaleta = [...new Set(Object.values(MODELOS))].map((a) => [a, true]);
  const proprios = Object.values(COM_TEXTURA_PROPRIA).map((a) => [a, false]);
  const tudo = [...comPaleta, ...proprios];

  let prontos = 0;

  await Promise.all(
    tudo.map(async ([arquivo, usarPaleta]) => {
      const promessa = carregar(arquivo, usarPaleta);
      promessa.modeloPronto = await promessa;
      prontos += 1;
      aoProgredir?.(prontos, tudo.length);
    })
  );

  console.info(
    `FAZENDA: ${prontos} modelos carregados — ${comPaleta.length} na paleta ` +
      `compartilhada, ${proprios.length} com textura propria (4 texturas na GPU).`
  );
  return prontos;
}
